import { ScrollArea } from "@timeghost/ui/shadcn/scroll-area";
import type { JSX, ReactNode } from "react";
import ContentHeader from "@/components/shell/content-header";
import { cn } from "@/lib/utils";

export interface BoxedPageProps {
	readonly actions?: ReactNode;
	readonly additionalPadding?: boolean;
	readonly bodyClassName?: string;
	/**
	 * `scroll` (default): ContentHeader sticky; body ScrollArea under header.
	 * `fill`: ContentHeader sticky; body is flex overflow-hidden for nested ScrollArea
	 * (virtual lists). Never put ContentHeader inside ScrollArea.
	 */
	readonly bodyMode?: "scroll" | "fill";
	readonly children: ReactNode;
	readonly className?: string;
	readonly description?: ReactNode;
	/** Class for header (e.g. tab list). */
	readonly headerClassName?: string;
	/** Replaces title/description in ContentHeader (e.g. tab list). */
	readonly headerContent?: ReactNode;
	/** `tabs`: header hugs the tab strip so the active underline sits on the divider. */
	readonly headerVariant?: "default" | "tabs";
	/** Floats inside the boxed panel (e.g. record toolbar). Not inside ScrollArea. */
	readonly overlay?: ReactNode;
	/** Sticky strip under ContentHeader (e.g. filters). */
	readonly subheader?: ReactNode;
	/** Class for subheader (e.g. filters). */
	readonly subheaderClassName?: string;
	readonly title?: ReactNode;
}

/** Page shell: sticky ContentHeader (+ optional subheader) + scroll/fill body. */
export default function BoxedPage({
	title,
	description,
	actions,
	headerContent,
	headerVariant = "default",
	subheader,
	children,
	className,
	headerClassName,
	bodyClassName,
	bodyMode = "scroll",
	subheaderClassName,
	overlay,
	additionalPadding = true,
}: BoxedPageProps): JSX.Element {
	return (
		<div
			className={cn("relative flex min-h-0 min-w-0 flex-1 flex-col overflow-clip", className)}
			data-slot="boxed-page">
			<ContentHeader
				actions={actions}
				className={cn(headerContent ? "border-b-0" : undefined, headerClassName)}
				content={headerContent}
				contentVariant={headerVariant}
				description={description}
				title={title}
			/>
			{subheader ? (
				<div
					className={cn(
						"flex shrink-0 flex-wrap items-start gap-2 border-border border-b bg-boxed-background px-4 py-2",
						subheaderClassName
					)}
					data-slot="boxed-page-subheader">
					{subheader}
				</div>
			) : null}
			{bodyMode === "scroll" ? (
				<ScrollArea
					className="h-0 min-h-0 w-full min-w-0 flex-1 overflow-auto"
					data-slot="boxed-page-body"
					viewportClassName={cn("overflow-anchor-none", additionalPadding && "pb-60")}>
					<div className={cn("p-4 pb-[max(1rem,var(--record-toolbar-clearance,1rem))]", bodyClassName)}>{children}</div>
				</ScrollArea>
			) : (
				<div
					className={cn(
						"flex h-0 min-h-0 w-full min-w-0 flex-1 flex-col overflow-clip p-4 pb-[max(1rem,var(--record-toolbar-clearance,1rem))]",
						bodyClassName
					)}
					data-slot="boxed-page-body">
					{children}
				</div>
			)}
			{overlay ? (
				<div className="pointer-events-none absolute inset-x-0 bottom-0 z-20" data-slot="boxed-page-overlay">
					{overlay}
				</div>
			) : null}
		</div>
	);
}
